import { ReactNode, useContext } from "react";
import { Navigate } from "react-router-dom";
import { Spin } from "antd";

import AppContext from "../../context/AppContext";
import { isEntryProductReachable } from "../../routing/guardTriage";
import { findFirstMatchingRoute } from "../../routing/redirectPriority";
import {
  hasValidAffiliatePortalSession,
  hasRegulatorPortalSession,
  isTokenExpired,
} from "../../utils/tokenUtils";
import { AFFILIATE_PORTAL_DASHBOARD_ROUTE } from "../../routing/affiliatePortalRoutes";
import { REGULATOR_PORTAL_DASHBOARD_ROUTE } from "../RegulatorPortal/regulatorPortalRoutes";

/**
 * Reverse of `RequireAuth`: guards the public login route (`/`) so an admin who
 * already has a valid session isn't shown the login form again after reopening
 * the window/tab.
 *
 * Resolution order (sessions are mutually exclusive — `LogIn` wipes the other
 * stores on every sign-in — but we resolve the synchronous portal checks
 * first so we never flash a spinner for a portal user):
 *  1. Valid affiliate portal session -> `/affiliate-portal/dashboard`.
 *  2. Regulator portal session -> `/regulator-portal/dashboard`. This is a
 *     structural check only (is there an unexpired regulator token to ask
 *     with?); whether the caller really is a regulator, and whether they are
 *     approved, is `RequireRegulatorAuth`'s call to make off `/me`. It clears
 *     the session when `/me` says no, so a stale token can't loop back here.
 *  3. Valid staff session -> wait for the admin profile to load, then walk
 *     `REDIRECT_PRIORITY` (in `src/routing/redirectPriority.ts`, Sider tab
 *     order) and land on the highest-priority section the admin can
 *     access. If they don't match any sider tab the session is treated
 *     as invalid and the login form is rendered.
 *  4. Otherwise render the login screen.
 *
 * A staff `AccessToken` that is expired but still has a `RefreshToken` is
 * treated as valid (the 401 interceptor refreshes it), mirroring `RequireAuth`.
 */

type StoredToken = {
  AccessToken?: string;
  RefreshToken?: string;
} | null;

type AppContextShape = {
  adminPermissions: string[] | null;
  adminDataLoaded: boolean;
};

type RedirectIfAuthenticatedProps = {
  children: ReactNode;
};

const getStaffToken = (): StoredToken => {
  const rawToken = localStorage.getItem("token");
  if (!rawToken) return null;
  try {
    return JSON.parse(rawToken) as StoredToken;
  } catch {
    // Malformed token (corrupted storage / old format). Treat as no session
    // so the login screen renders instead of crashing the tree.
    return null;
  }
};

const clearStaffSession = (): void => {
  // Mirrors the cleanup performed by `LogIn.js` so an admin whose payload
  // doesn't unlock any sider tab can sign in again with a usable role
  // instead of looping through this guard.
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("lastLogInTime");
};

const RedirectIfAuthenticated = ({
  children,
}: RedirectIfAuthenticatedProps) => {
  const { adminPermissions, adminDataLoaded } = useContext(
    AppContext,
  ) as AppContextShape;

  if (hasValidAffiliatePortalSession()) {
    return <Navigate to={AFFILIATE_PORTAL_DASHBOARD_ROUTE} replace />;
  }

  if (hasRegulatorPortalSession()) {
    return <Navigate to={REGULATOR_PORTAL_DASHBOARD_ROUTE} replace />;
  }

  const staffToken = getStaffToken();
  const hasValidStaffSession =
    !!staffToken?.AccessToken &&
    !(isTokenExpired() && !staffToken.RefreshToken);

  if (!hasValidStaffSession) {
    return <>{children}</>;
  }

  // Authenticated staff: wait for the profile so we can route to the correct
  // landing page instead of flashing the login form or guessing the route.
  if (!adminDataLoaded) {
    return (
      <div
        data-testid="redirect-if-authenticated-loading"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Reachable-first; the unrestricted second walk keeps the sign-out branch
  // reserved for genuinely unrecognised permission sets.
  const matchedRoute =
    findFirstMatchingRoute(adminPermissions, {
      isEntryReachable: isEntryProductReachable,
    }) ?? findFirstMatchingRoute(adminPermissions);

  if (!matchedRoute) {
    // Authenticated but holds no permission recognised by the Sider —
    // treat the session as invalid (role catalog drift, BE stripped roles,
    // etc.) and render the login form. We clear the staff session here so
    // we don't loop back into this guard on the next render.
    clearStaffSession();
    return <>{children}</>;
  }

  return <Navigate to={matchedRoute} replace />;
};

export default RedirectIfAuthenticated;
